﻿import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { InitialsAvatar } from "@/components/common/InitialsAvatar";
import { dashboardProfileMock } from "@/data/dashboardProfileMockData";
import { userAvatar } from "@/utils/userAvatar";
import {
  Bell,
  Loader2,
  Mic,
  Power,
  Settings,
  UserCircle,
  RotateCw,
} from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Lazy loading для модулей - загружаются только при необходимости
const AIResultsModule = lazy(() =>
  import("@/components/modules/AIResultsModule").then((m) => ({
    default: m.AIResultsModule,
  })),
);
const ResonanceEventsV5Module = lazy(() =>
  import("@/components/modules/ResonanceEventsV5Module").then((m) => ({
    default: m.ResonanceEventsV5Module,
  })),
);
const UserProfileModule = lazy(() =>
  import("@/components/modules/UserProfileModule").then((m) => ({
    default: m.UserProfileModule,
  })),
);
const AdministrationModule = lazy(() =>
  import("@/components/modules/AdministrationModule").then((m) => ({
    default: m.AdministrationModule,
  })),
);
const UsersPersonsModule = lazy(() =>
  import("@/components/modules/admin/UsersPersonsModule").then((m) => ({
    default: m.UsersPersonsModule,
  })),
);
const OrganizationsModule = lazy(() =>
  import("@/components/modules/admin/OrganizationsModule").then((m) => ({
    default: m.OrganizationsModule,
  })),
);
const OrganizationTypesModule = lazy(() =>
  import("@/components/modules/admin/OrganizationTypesModule").then((m) => ({
    default: m.OrganizationTypesModule,
  })),
);
const PositionsModule = lazy(() =>
  import("@/components/modules/admin/PositionsModule").then((m) => ({
    default: m.PositionsModule,
  })),
);
const MonitoringAuditModule = lazy(() =>
  import("@/components/modules/admin/MonitoringAuditModule").then((m) => ({
    default: m.MonitoringAuditModule,
  })),
);
const OperationalAnalysisModule = lazy(
  () =>
    import("@/components/modules/operational-analysis/OperationalAnalysisModule"),
);

const OperationalSummaryUploadModule = lazy(() =>
  import("@/components/modules/admin/OperationalSummaryUploadModule").then(
    (m) => ({
      default: m.OperationalSummaryUploadModule,
    }),
  ),
);
const ProxySettingsModule = lazy(() =>
  import("@/components/modules/admin/ProxySettingsModule").then((m) => ({
    default: m.ProxySettingsModule,
  })),
);
const PermissionsModule = lazy(() =>
  import("@/components/modules/admin/PermissionsModule").then((m) => ({
    default: m.PermissionsModule,
  })),
);
const CrimeModule = lazy(() =>
  import("@/components/modules/CrimeModule").then((m) => ({
    default: m.CrimeModule,
  })),
);
const CrimeV2Module = lazy(() =>
  import("@/components/modules/CrimeV2Module").then((m) => ({
    default: m.CrimeV2Module,
  })),
);
const CrimeV3Module = lazy(() =>
  import("@/components/modules/CrimeV3Module").then((m) => ({
    default: m.CrimeV3Module,
  })),
);
const CrimeV4Module = lazy(() =>
  import("@/components/modules/CrimeV4Module").then((m) => ({
    default: m.CrimeV4Module,
  })),
);
const CrimeStatisticsModule = lazy(() =>
  import("@/components/modules/CrimeStatisticsModule").then((m) => ({
    default: m.CrimeStatisticsModule,
  })),
);
const OperationalSummaryModule = lazy(() =>
  import("@/components/modules/OperationalSummaryModule").then((m) => ({
    default: m.OperationalSummaryModule,
  })),
);
const InvestorsProjectsModule = lazy(() =>
  import("@/components/modules/InvestorsProjectsModule").then((m) => ({
    default: m.InvestorsProjectsModule,
  })),
);
const BusinessProtectionModule = lazy(() =>
  import("@/components/modules/BusinessProtectionModule").then((m) => ({
    default: m.BusinessProtectionModule,
  })),
);
const BusinessInvestorsModule = lazy(() =>
  import("@/components/modules/BusinessInvestorsModule").then((m) => ({
    default: m.BusinessInvestorsModule,
  })),
);
const SupervisionControlModule = lazy(() =>
  import("@/components/modules/SupervisionControlModule").then((m) => ({
    default: m.SupervisionControlModule,
  })),
);
const PrisonSupervisionModule = lazy(() =>
  import("@/components/modules/PrisonSupervisionModule").then((m) => ({
    default: m.PrisonSupervisionModule,
  })),
);
const StateProsecutorModule = lazy(() =>
  import("@/components/modules/StateProsecutorModule").then((m) => ({
    default: m.StateProsecutorModule,
  })),
);
const JudicialSupervisionModule = lazy(() =>
  import("@/components/modules/JudicialSupervisionModule").then((m) => ({
    default: m.JudicialSupervisionModule,
  })),
);
const ResocializationModule = lazy(() =>
  import("@/components/modules/ResocializationModule").then((m) => ({
    default: m.ResocializationModule,
  })),
);
const ExecutionModule = lazy(() =>
  import("@/components/modules/ExecutionModule").then((m) => ({
    default: m.ExecutionModule,
  })),
);
const ScheduleModule = lazy(() =>
  import("@/components/modules/ScheduleModule").then((m) => ({
    default: m.ScheduleModule,
  })),
);
const CitizensMessagesModule = lazy(() =>
  import("@/components/modules/CitizensMessagesModule").then((m) => ({
    default: m.CitizensMessagesModule,
  })),
);
const AppealsModule = lazy(() =>
  import("@/components/modules/AppealsModule").then((m) => ({
    default: m.AppealsModule,
  })),
);
const MyTasksPage = lazy(() =>
  import("@/components/pages/my-tasks/MyTasksPage").then((m) => ({
    default: m.MyTasksPage,
  })),
);
const StateSearchModule = lazy(() =>
  import("@/components/modules/StateSearchModule").then((m) => ({
    default: m.StateSearchModule,
  })),
);
const SearchModule = lazy(() =>
  import("@/components/modules/SearchModule").then((m) => ({
    default: m.SearchModule,
  })),
);
const InterstateSearchModule = lazy(() =>
  import("@/components/modules/InterstateSearchModule").then((m) => ({
    default: m.InterstateSearchModule,
  })),
);
const InternationalSearchModule = lazy(() =>
  import("@/components/modules/InternationalSearchModule").then((m) => ({
    default: m.InternationalSearchModule,
  })),
);
const InternalSecurityModule = lazy(() =>
  import("@/components/modules/InternalSecurityModule").then((m) => ({
    default: m.InternalSecurityModule,
  })),
);
const SocialDemographicModule = lazy(() =>
  import("@/components/modules/SocialDemographicModule").then((m) => ({
    default: m.SocialDemographicModule,
  })),
);
const SocialDemographicOverviewModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicOverviewModule").then(
    (m) => ({
      default: m.SocialDemographicOverviewModule,
    }),
  ),
);
const SocialDemographicChildrenModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicChildrenModule").then(
    (m) => ({
      default: m.SocialDemographicChildrenModule,
    }),
  ),
);
const SocialDemographicVulnerableModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicVulnerableModule").then(
    (m) => ({
      default: m.SocialDemographicVulnerableModule,
    }),
  ),
);
const SocialDemographicMobilityModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicMobilityModule").then(
    (m) => ({
      default: m.SocialDemographicMobilityModule,
    }),
  ),
);
const SocialDemographicEconomicallyActiveModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicEconomicallyActiveModule").then(
    (m) => ({
      default: m.SocialDemographicEconomicallyActiveModule,
    }),
  ),
);
const SocialDemographicElderlyModule = lazy(() =>
  import("@/components/modules/social-demographic/SocialDemographicElderlyModule").then(
    (m) => ({
      default: m.SocialDemographicElderlyModule,
    }),
  ),
);

// Эти компоненты загружаются сразу (часто используемые)
import { AIAssistant } from "@/components/ai/AIAssistant";
import { ChunkErrorBoundary } from "@/components/common/ChunkErrorBoundary";
import { Footer } from "@/components/layout/Footer";
import { UserProfileModal } from "@/components/layout/UserProfileModal";
import { useAIAssistant } from "@/hooks/useAIAssistant";

// Страницы - lazy loading
const HomePage = lazy(() =>
  import("@/components/pages/HomePage").then((m) => ({ default: m.HomePage })),
);
const QuickContactPage = lazy(() =>
  import("@/components/pages/contacts/QuickContactPage").then((m) => ({
    default: m.QuickContactPage,
  })),
);
const StateBodiesPage = lazy(() =>
  import("@/components/pages/contacts/StateBodiesPage").then((m) => ({
    default: m.StateBodiesPage,
  })),
);
const GovBodyDetailPage = lazy(() =>
  import("@/components/pages/contacts/GovBodyDetailPage").then((m) => ({
    default: m.GovBodyDetailPage,
  })),
);
const EmployeeCardPage = lazy(() =>
  import("@/components/pages/contacts/EmployeeCardPage").then((m) => ({
    default: m.EmployeeCardPage,
  })),
);
const PersonnelDashboardPage = lazy(() =>
  import("@/components/pages/personnel/PersonnelDashboardPage").then((m) => ({
    default: m.PersonnelDashboardPage,
  })),
);
const NetworkLogsPage = lazy(() =>
  import("@/components/pages/NetworkLogsPage").then((m) => ({
    default: m.NetworkLogsPage,
  })),
);
const SummaryModule = lazy(() =>
  import("@/components/modules/summary/SummaryModule").then((m) => ({
    default: m.SummaryModule,
  })),
);
const DashboardV2Page = lazy(() =>
  import("@/components/pages/dashboard-v2/DashboardV2Page").then((m) => ({
    default: m.DashboardV2Page,
  })),
);

// Компонент загрузки
const ModuleLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-sequence-primary" />
      <span className="text-sm text-gray-500">Загрузка модуля...</span>
    </div>
  </div>
);

import { BrandMark } from "@/components/brand/BrandMark";
import { BRAND_ASSETS } from "@/constants/brand";
import { ShellHeaderClock } from "@/components/layout/ShellHeaderClock";
// Силуэт, а не цветная эмблема: на подложке в 4% прозрачности залитый диск
// эмблемы дал бы бледное круглое пятно, а обводки читаются как водяной знак.
// @ts-ignore
// @ts-ignore
import aiArtIconWhite from "@/assets/images/ai-art-icon-white.png";

import { useSliceDispatch, useSliceSelector } from "@/store";
import { AdminViolationsV2Module } from "./modules/AdminViolationsV2Module";
import { isAkimSectorKey } from "@/constants/akimSectors";

const AkimSectorModule = lazy(() =>
  import("@/components/modules/akim/AkimSectorModule").then((m) => ({
    default: m.AkimSectorModule,
  })),
);

const PopulationDemographicsModule = lazy(() =>
  import("@/components/modules/akim/PopulationDemographicsModule").then((m) => ({
    default: m.PopulationDemographicsModule,
  })),
);

const HealthcareModule = lazy(() =>
  import("@/components/modules/akim/HealthcareModule").then((m) => ({
    default: m.HealthcareModule,
  })),
);

const PublicSafetyModule = lazy(() =>
  import("@/components/modules/akim/PublicSafetyModule").then((m) => ({
    default: m.PublicSafetyModule,
  })),
);

const EducationModule = lazy(() =>
  import("@/components/modules/akim/EducationModule").then((m) => ({
    default: m.EducationModule,
  })),
);

const LaborEmploymentModule = lazy(() =>
  import("@/components/modules/akim/LaborEmploymentModule").then((m) => ({
    default: m.LaborEmploymentModule,
  })),
);

const SocialProtectionModule = lazy(() =>
  import("@/components/modules/akim/SocialProtectionModule").then((m) => ({
    default: m.SocialProtectionModule,
  })),
);

const ElectionsModule = lazy(() =>
  import("@/components/modules/akim/ElectionsModule").then((m) => ({
    default: m.ElectionsModule,
  })),
);

const AkimAppealsModule = lazy(() =>
  import("@/components/modules/akim/AkimAppealsModule").then((m) => ({
    default: m.AkimAppealsModule,
  })),
);

// === Вспомогательные компоненты и элементы UI ===

export default function ProsecutorAnalyticsApp() {
  // === Навигация ===
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isTokenAuth, isTestModeAuth } = useAuth();

  // Определяем текущий раздел и подраздел
  const pathParts = location.pathname.split("/").filter(Boolean);
  const section = pathParts[0] || "base";
  const subSection = pathParts[1] || null;

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // === Проверка мобильного режима - приоритет URL, потом localStorage ===
  const [isMobile, setIsMobile] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlIsMobile = searchParams.get("is_mobile");

    // Если в URL есть параметр is_mobile - используем его и сохраняем
    if (urlIsMobile !== null) {
      const isMobileValue = urlIsMobile === "true";
      localStorage.setItem("isMobile", String(isMobileValue));
      return isMobileValue;
    }

    // Если в URL нет - проверяем localStorage
    const savedIsMobile = localStorage.getItem("isMobile");
    if (savedIsMobile !== null) {
      return savedIsMobile === "true";
    }

    // По умолчанию - false
    return false;
  });

  // Отслеживаем изменения URL и обновляем isMobile
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlIsMobile = searchParams.get("is_mobile");

    if (urlIsMobile !== null) {
      const isMobileValue = urlIsMobile === "true";
      localStorage.setItem("isMobile", String(isMobileValue));
      setIsMobile(isMobileValue);
    }
  }, [location.search]);

  // === Глобальные фильтры (шапка) ===
  const region = useSliceSelector("globalFilters", "selectedRegion");
  const { setSelectedRegion } = useSliceDispatch("globalFilters");

  const [period, setPeriod] = useState("month");

  const [year, setYear] = useState("2025");

  // === AI Assistant ===
  const {
    isOpen: isAIOpen,
    results: aiResults,
    openAssistant,
    closeAssistant,
    deleteResult,
  } = useAIAssistant();

  // === Данные пользователя ===
  const useProfileMock =
    isTestModeAuth ||
    !user?.fullName ||
    user.fullName.includes("TEST_MODE") ||
    user.fullName === "Загрузка...";

  const userData = {
    name: useProfileMock ? dashboardProfileMock.fullName : user!.fullName,
    position: useProfileMock
      ? dashboardProfileMock.position
      : user!.positionName || dashboardProfileMock.position,
    // Общий выбор с окном ассистента — см. `utils/userAvatar`.
    avatar: useProfileMock ? dashboardProfileMock.avatar : userAvatar(user),
  };

  // === Демоданные (в реальности придут из Data Lake) ===
  const isHomeLayout = section === "modules";
  const isBaseLayout = section === "base";
  const isStandaloneLayout = section === "summary";
  const isDashboardFlowSection =
    section === "schedule" ||
    section === "quick-contact" ||
    section === "state-bodies" ||
    section === "personnel" ||
    section === "tasks-v2" ||
    section === "contacts" ||
    section === "leaders";

  return (
    <div
      className={
        section === "summary"
          ? "text-gray-900 relative"
          : isHomeLayout || isBaseLayout || isDashboardFlowSection
            ? "text-gray-900 relative flex flex-col overflow-hidden h-[100dvh]"
            : "min-h-screen text-gray-900 relative flex flex-col"
      }
      style={
        {}
      }
    >
      {!isStandaloneLayout && (
        /*
         * Водяной знак фона — силуэт города «ATYRAU» (маска по альфе белого
         * логотипа, цвет — фирменный синий). Не <img>: белые линии на светлом
         * фоне не видны вовсе, а маска красит их в любой цвет.
         */
        <div
          aria-hidden
          className="fixed left-[10%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[540px] opacity-[0.05] pointer-events-none z-0"
          style={{
            backgroundColor: "var(--dg-teal)",
            WebkitMaskImage: `url(${BRAND_ASSETS.mark})`,
            WebkitMaskSize: "contain",
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            maskImage: `url(${BRAND_ASSETS.mark})`,
            maskSize: "contain",
            maskRepeat: "no-repeat",
            maskPosition: "center",
          }}
        />
      )}
      {/* === Шапка === */}

      {/* Скрываем шапку при мобильном режиме, token auth или на странице summary */}
      {!isMobile && !isTokenAuth && !isStandaloneLayout && (
        <header className="shell-brand shell-header sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 ipad:px-6 py-1.5 ipad:py-2">
            <div className="flex items-center justify-between relative min-h-[52px] ipad:min-h-[56px]">
              <div className="flex items-center gap-3 ipad:gap-5 min-w-0">
                <button
                  type="button"
                  onClick={() => navigate("/base")}
                  className="flex items-center rounded-cn-lg hover:bg-[rgba(14,159,144,0.1)] transition-colors -ml-1 px-2 py-0 ipad:px-2.5 shrink-0"
                  aria-label={isBaseLayout ? "Главная страница" : "На главную страницу"}
                >
                  <BrandMark variant="dark" showPanelTitle size="lg" />
                </button>

                {isBaseLayout && (
                  <>
                    <div className="shell-header-divider" aria-hidden />
                    <ShellHeaderClock variant="dark" className="min-w-0" />
                  </>
                )}
              </div>

              {/* Пользователь */}
              <div className="relative flex items-center gap-2 shrink-0 ml-3">
                {/* Иконки уведомлений и звонков слева от ФИО */}
                <div className="hidden ipad:flex items-center gap-1.5">
                  <button
                    className="p-2.5 rounded-lg hover:bg-[rgba(14,159,144,0.1)] transition-colors flex items-center justify-center"
                    aria-label="Уведомления"
                  >
                    <Bell size={24} className="text-[var(--dg-teal-ink)]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/quick-contact")}
                    className="tab-ai-fab px-3 py-2 rounded-lg flex items-center gap-2 transition-all duration-300 border-0"
                    aria-label="Быстрая связь"
                    title="Быстрая связь — голосовое сообщение"
                  >
                    {/* Подпись — от альбомной ориентации; в книжной шапке тесно, остаётся иконка. */}
                    <span className="hidden ipad-landscape:inline text-base font-medium text-white whitespace-nowrap">
                      Быстрая связь
                    </span>
                    <Mic size={22} className="text-white shrink-0" />
                  </button>
                </div>

                <div className="shell-header-divider hidden ipad:block mx-1" aria-hidden />

                {/* Информация о пользователе + меню */}
                <div className="flex items-center gap-3 relative">
                  <div className="relative">
                    <button
                      type="button"
                      className={`flex items-center gap-2 focus:outline-none rounded-lg p-2 transition-all ${
                        showUserMenu
                          ? "bg-[rgba(14,159,144,0.12)] ring-2 ring-[rgba(14,159,144,0.3)]"
                          : "hover:bg-[rgba(14,159,144,0.08)]"
                      }`}
                      onClick={() => setShowUserMenu((prev) => !prev)}
                      aria-label="Открыть меню пользователя"
                    >
                      {/* ФИО и должность — от альбомной ориентации: в книжной они наезжали на часы. Аватар и меню остаются. */}
                      <div className="text-right hidden ipad-landscape:block">
                        <div className="text-base font-semibold text-[var(--dg-chalk)]">
                          {userData.name}
                        </div>
                        <div className="text-xs text-[var(--dg-silt)] whitespace-pre-line">
                          {userData.position}
                        </div>
                      </div>
                      {userData.avatar ? (
                        <img
                          src={userData.avatar}
                          alt={userData.name}
                          className={`w-12 h-12 ipad:w-14 ipad:h-14 rounded-full object-cover border-2 transition-all ${
                            showUserMenu ? "border-[var(--dg-teal)]" : "border-[rgba(14,159,144,0.35)]"
                          }`}
                        />
                      ) : (
                        <div
                          className={`w-12 h-12 ipad:w-14 ipad:h-14 rounded-full bg-white flex items-center justify-center border-2 transition-all text-[var(--dg-teal-ink)] ${
                            showUserMenu ? "border-[var(--dg-teal)]" : "border-[rgba(14,159,144,0.35)]"
                          }`}
                        >
                          <InitialsAvatar name={userData.name} fontSize={18} />
                        </div>
                      )}
                    </button>

                    {/* Dropdown меню */}
                    {showUserMenu && (
                      <>
                        {/* Overlay для закрытия при клике вне */}
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowUserMenu(false)}
                        />

                        {/* Само меню */}
                        <Card className="absolute right-0 top-full mt-2 w-56 shadow-lg border border-gray-200 overflow-hidden z-50">
                          <div className="py-1">
                            {/* Кнопка администрирования */}
                            <button
                              type="button"
                              onClick={() => {
                                setShowUserMenu(false);
                                navigate("/administration");
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center gap-3 transition-colors"
                            >
                              <Settings
                                size={18}
                                className="text-sequence-primary flex-shrink-0"
                              />
                              <span className="font-medium text-gray-900">
                                Администрирование
                              </span>
                            </button>

                            {/* Разделитель */}
                            <div className="border-t border-gray-200 my-1" />

                            {/* Кнопка профиля */}
                            <button
                              type="button"
                              onClick={() => {
                                setShowUserMenu(false);
                                navigate("/user-profile");
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center gap-3 transition-colors"
                            >
                              <UserCircle
                                size={18}
                                className="text-sequence-primary flex-shrink-0"
                              />
                              <span className="font-medium text-gray-900">
                                Профиль пользователя
                              </span>
                            </button>

                            {/* Разделитель */}
                            <div className="border-t border-gray-200 my-1" />

                            {/*
                             * Перезагрузка страницы.
                             *
                             * `location.reload()`, А НЕ ПЕРЕХОД РОУТЕРОМ. Нужен
                             * полный перезапуск: планшет стоит открытым сутками,
                             * за это время протухает access-токен (900 секунд),
                             * рвётся сеть до стенда и застревают ответы в кэше
                             * запросов. Навигация внутри приложения ничего из
                             * этого не чинит — состояние переживает переход.
                             *
                             * На планшете нет ни клавиатуры с F5, ни адресной
                             * строки: без этой кнопки страницу перезагрузить
                             * нечем, и единственным способом остаётся выход из
                             * системы с повторным входом.
                             */}
                            <button
                              type="button"
                              onClick={() => {
                                setShowUserMenu(false);
                                window.location.reload();
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center gap-3 transition-colors"
                            >
                              <RotateCw
                                size={18}
                                className="text-sequence-primary flex-shrink-0"
                              />
                              <span className="font-medium text-gray-900">
                                Перезагрузить
                              </span>
                            </button>

                            {/* Разделитель */}
                            <div className="border-t border-gray-200 my-1" />

                            {/* Кнопка выхода */}
                            <button
                              type="button"
                              onClick={() => {
                                setShowUserMenu(false);
                                // Очищаем localStorage при ручном logout
                                localStorage.clear();
                                logout();
                                navigate("/login", { replace: true });
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 active:bg-red-100 flex items-center gap-3 transition-colors"
                            >
                              <Power size={18} className="flex-shrink-0" />
                              <span className="font-medium">
                                Выход из системы
                              </span>
                            </button>
                          </div>
                        </Card>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </header>
      )}

      {/* === Главная страница === */}

      {section === "modules" && (
        <ChunkErrorBoundary>
          <Suspense fallback={<ModuleLoader />}>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <HomePage />
            </div>
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {section === "base" && (
        <ChunkErrorBoundary>
          <Suspense fallback={<ModuleLoader />}>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <DashboardV2Page />
            </div>
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {isDashboardFlowSection && (
        <ChunkErrorBoundary>
          <Suspense fallback={<ModuleLoader />}>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {section === "schedule" && (
                <ScheduleModule onBack={() => navigate("/base")} />
              )}
              {section === "quick-contact" && <QuickContactPage />}
              {section === "state-bodies" && !subSection && <StateBodiesPage />}
              {section === "state-bodies" && subSection && (
                <GovBodyDetailPage govBodyId={subSection} />
              )}
              {section === "personnel" && <PersonnelDashboardPage />}
              {section === "tasks-v2" && <MyTasksPage />}
              {section === "contacts" && subSection && <EmployeeCardPage />}
              {section === "leaders" && <QuickContactPage />}
            </div>
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {/* === Разделы системы === */}

      {/* Для summary рендерим отдельно без main и padding */}
      {section === "summary" ? (
        <ChunkErrorBoundary>
          <Suspense fallback={<ModuleLoader />}>
            <SummaryModule />
          </Suspense>
        </ChunkErrorBoundary>
      ) : section !== "modules" && section !== "base" && !isDashboardFlowSection ? (
        <ChunkErrorBoundary>
          <Suspense fallback={<ModuleLoader />}>
            <main className="mx-auto max-w-7xl px-4 py-6 space-y-8 flex-1 relative">
              {/* Отраслевые модули акима (кроме выделенных контуров) */}
              {isAkimSectorKey(section) &&
                section !== "population" &&
                section !== "education" &&
                section !== "labor" &&
                section !== "social-protection" &&
                section !== "appeals" &&
                section !== "public-safety" &&
                section !== "business-investors" &&
                section !== "elections" &&
                section !== "healthcare" && (
                <AkimSectorModule
                  sectorKey={section}
                  onBack={() => navigate("/modules")}
                />
              )}

              {section === "healthcare" && (
                <HealthcareModule onBack={() => navigate("/modules")} />
              )}

              {section === "population" && (
                <PopulationDemographicsModule
                  onBack={() => navigate("/modules")}
                />
              )}

              {section === "education" && (
                <EducationModule onBack={() => navigate("/modules")} />
              )}

              {section === "labor" && (
                <LaborEmploymentModule onBack={() => navigate("/modules")} />
              )}

              {section === "social-protection" && (
                <SocialProtectionModule onBack={() => navigate("/modules")} />
              )}

              {section === "elections" && (
                <ElectionsModule onBack={() => navigate("/modules")} />
              )}

              {section === "appeals" && (
                <AkimAppealsModule onBack={() => navigate("/modules")} />
              )}

              {section === "public-safety" && (
                <PublicSafetyModule onBack={() => navigate("/modules")} />
              )}

              {/* Преступность - главный модуль с выбором подмодулей */}
              {section === "crime" && !subSection && (
                <CrimeV4Module
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Преступность - подмодули */}
              {section === "crime-application" && (
                <CrimeModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/crime")}
                />
              )}

              {section === "crime-investigation" && (
                <CrimeV2Module
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/crime")}
                />
              )}

              {section === "crime-map" && (
                <CrimeV3Module
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/crime")}
                />
              )}

              {section === "crime-statistics" && (
                <CrimeStatisticsModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/crime")}
                />
              )}

              {/* Резонансные события и инциденты */}

              {section === "critical-events" && (
                <ResonanceEventsV5Module
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/base")}
                />
              )}

              {/* Оперативный анализ */}

              {/* Экран был закомментирован при переносе планшета: его вкладки
                  читали ручки надзорного сервера и оставались пустыми. Теперь
                  это оперативная обстановка области на своих данных — см.
                  `operational-analysis/OperationalAnalysisModule`. */}
              {section === "operational-analysis" && (
                <OperationalAnalysisModule onBack={() => navigate("/modules")} />
              )}

              {/* Административные нарушения */}

              {section === "admin-violations-v2" && (
                <AdminViolationsV2Module
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Социально-демографическая обстановка */}

              {section === "social-demographic" && !subSection && (
                <SocialDemographicModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Социально-демографическая обстановка — подмодули */}
              {section === "social-demographic-overview" && (
                <SocialDemographicOverviewModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {section === "social-demographic-children" && (
                <SocialDemographicChildrenModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {section === "social-demographic-vulnerable" && (
                <SocialDemographicVulnerableModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {section === "social-demographic-mobility" && (
                <SocialDemographicMobilityModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {section === "social-demographic-economically-active" && (
                <SocialDemographicEconomicallyActiveModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {section === "social-demographic-elderly" && (
                <SocialDemographicElderlyModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  year={year}
                  onYearChange={setYear}
                  onBack={() => navigate("/social-demographic")}
                />
              )}

              {/* Ресоциализация и пробация - главный модуль с подмодулями */}

              {section === "execution" && (
                <ExecutionModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Судебная практика (подмодуль) */}

              {section === "state-prosecutor" && (
                <StateProsecutorModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/execution")}
                />
              )}

              {/* Судебные решения (подмодуль) */}

              {section === "judicial-supervision" && (
                <JudicialSupervisionModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/execution")}
                />
              )}

              {/* Контроль исполнения установленных ограничений (подмодуль) */}

              {section === "supervision-control" && (
                <SupervisionControlModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/execution")}
                />
              )}

              {/* Учреждения УИС области (подмодуль) */}

              {section === "prison-supervision" && (
                <PrisonSupervisionModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  onBack={() => navigate("/execution")}
                />
              )}

              {/* Ресоциализация (подмодуль) */}

              {section === "resocialization" && (
                <ResocializationModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/execution")}
                />
              )}

              {/* Инвесторы и проекты */}

              {/* === Мой график === */}

              {section === "network-logs" && (
                <NetworkLogsPage onBack={() => navigate("/modules")} />
              )}

              {section === "administration-ai-results" && (
                <AIResultsModule
                  results={aiResults}
                  onBack={() => navigate("/administration")}
                  onDeleteResult={deleteResult}
                />
              )}

              {section === "operational-summary" && (
                <OperationalSummaryModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Сообщения граждан (101 / 102 / 103 / 109 / 112) */}
              {section === "citizens-messages" && (
                <CitizensMessagesModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Обращения — см. AkimAppealsModule выше */}

              {section === "user-profile" && (
                <UserProfileModule onBack={() => navigate("/modules")} />
              )}

              {/* Администрирование */}
              {section === "administration" && !subSection && (
                <AdministrationModule onBack={() => navigate("/modules")} />
              )}

              {section === "administration-users-persons" && (
                <UsersPersonsModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-organizations" && (
                <OrganizationsModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-organization-types" && (
                <OrganizationTypesModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-positions" && (
                <PositionsModule onBack={() => navigate("/administration")} />
              )}

              {section === "administration-monitoring-audit" && (
                <MonitoringAuditModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-operational-summary-upload" && (
                <OperationalSummaryUploadModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-proxy-settings" && (
                <ProxySettingsModule
                  onBack={() => navigate("/administration")}
                />
              )}

              {section === "administration-permissions" && (
                <PermissionsModule onBack={() => navigate("/administration")} />
              )}

              {section === "business-investors" && (
                <BusinessInvestorsModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {section === "investors" && (
                <InvestorsProjectsModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/business-investors")}
                />
              )}

              {section === "business" && (
                <BusinessProtectionModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/business-investors")}
                />
              )}

              {/* Мониторинг служебной деятельности сотрудников */}
              {section === "internal-security" && (
                <InternalSecurityModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Розыск - главный модуль с подмодулями */}
              {section === "search" && (
                <SearchModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/modules")}
                />
              )}

              {/* Государственный розыск - подмодуль (для обратной совместимости) */}
              {section === "state-search" && (
                <StateSearchModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/search")}
                />
              )}

              {/* Межгосударственный розыск - подмодуль */}
              {section === "interstate-search" && (
                <InterstateSearchModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/search")}
                />
              )}

              {/* Международный розыск - подмодуль */}
              {section === "international-search" && (
                <InternationalSearchModule
                  region={region}
                  onRegionChange={setSelectedRegion}
                  period={period}
                  onPeriodChange={setPeriod}
                  onBack={() => navigate("/search")}
                />
              )}
            </main>
          </Suspense>
        </ChunkErrorBoundary>
      ) : null}

      {/* === Футер === */}
      {!isStandaloneLayout && <Footer />}

      {/* === Плавающая кнопка AI Ассистента (справа снизу) === */}
      {!isStandaloneLayout && (
        <div
          className="fixed bottom-6 right-6 ipad:bottom-8 ipad:right-8 z-50"
          style={{ position: "fixed" }}
        >
          <button
            aria-label="Открыть AI Ассистента"
            onClick={openAssistant}
            className="tab-ai-fab group relative flex items-center justify-center w-16 h-16 ipad:w-20 ipad:h-20 rounded-full transition-all duration-300 ease-in-out hover:scale-110 active:scale-95"
            style={{
              animation: "breathe 3s ease-in-out infinite",
            }}
          >
            {/* Внешнее свечение */}
            <div className="tab-ai-fab__glow absolute inset-0 rounded-full opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300"></div>

            {/* Иконка */}
            <img
              src={aiArtIconWhite}
              alt="AI Ассистент"
              className="relative z-10 w-8 h-8 ipad:w-11 ipad:h-11 object-contain filter drop-shadow-lg"
            />
          </button>
        </div>
      )}

      {/* === AI Assistant Modal === */}
      {section !== "summary" && (
        <AIAssistant isOpen={isAIOpen} onClose={closeAssistant} />
      )}

      {/* === User Profile Modal === */}
      {section !== "summary" && showProfileModal && (
        <UserProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}
